import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bell, LogOut, UserRound, BarChart3, Gift, ChevronRight, ArrowLeftRight,
  LifeBuoy, BookOpen, Sparkles, Activity, FileText, Lock, ChevronDown,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";

/**
 * Harvest-style user menu that lives in the sidebar footer.
 *
 * Props:
 *   onOpenWorkspaceSwitcher() — parent controls the workspace popover so we can
 *                               reuse the one that already renders at the top.
 */
export default function UserMenu({ onOpenWorkspaceSwitcher, alertsCount = 0 }) {
  const nav = useNavigate();
  const { toast } = useToast();
  const { user, activeWorkspace, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false); setHelpOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const initials = (user?.name || user?.email || "?")
    .split(/[.\s@]/).filter(Boolean).slice(0, 2)
    .map((s) => s[0]?.toUpperCase()).join("");

  const soon = (feature) => {
    toast?.({
      title: `${feature} is on the way`,
      description: "This screen ships in a follow-up release. Ping us if you need it faster.",
    });
    setOpen(false); setHelpOpen(false);
  };

  const goto = (path) => { nav(path); setOpen(false); setHelpOpen(false); };
  const releaseNotesJump = () => {
    // Dashboard's What's new panel doubles as our release notes for now
    nav("/app");
    setTimeout(() => {
      document.querySelector('[data-testid="announcements-panel"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    setOpen(false); setHelpOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      {/* Trigger row */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="user-menu-trigger"
        className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left hover:bg-white/5"
      >
        <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-semibold shrink-0">
          {initials || <UserRound size={14} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{user?.name || user?.email || "You"}</div>
          <div className="text-[10px] text-white/50 truncate">{activeWorkspace?.name || ""}</div>
        </div>
        <div className="relative text-white/70">
          <Bell size={14} />
          {alertsCount > 0 && (
            <span
              data-testid="user-menu-alerts-dot"
              className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-coral" />
          )}
        </div>
      </button>

      {open && (
        <div
          data-testid="user-menu-popover"
          className="absolute z-30 left-2 right-2 bottom-full mb-2 bg-white text-[#1F2F27] rounded-xl border border-cream shadow-xl overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-cream flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-cream-2 flex items-center justify-center text-xs font-semibold text-forest">
              {initials || <UserRound size={14} />}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name || user?.email || "You"}</div>
              <div className="text-[11px] text-[#6a736f] truncate">{activeWorkspace?.name || "—"}</div>
            </div>
          </div>

          {/* Primary items */}
          <MenuItem icon={UserRound} onClick={() => goto("/app/settings")} testid="menu-my-profile">
            My profile
          </MenuItem>
          <MenuItem icon={BarChart3} onClick={() => goto("/app/reports/mine")} testid="menu-my-time-report">
            My time report
          </MenuItem>
          <MenuItem icon={Bell} onClick={() => soon("Notifications preferences")} testid="menu-notifications" trail={alertsCount > 0 ? String(alertsCount) : null}>
            Notifications
          </MenuItem>
          <MenuItem icon={Gift} onClick={() => soon("Refer a friend")} testid="menu-refer-friend">
            Refer a friend
          </MenuItem>

          {/* Need help? with nested submenu */}
          <button
            onClick={() => setHelpOpen((v) => !v)}
            data-testid="menu-need-help"
            className="w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-cream-2/60"
          >
            <span className="flex items-center gap-2 text-forest">
              <LifeBuoy size={14} /> Need help?
            </span>
            <ChevronRight size={12} className={`transition-transform text-[#6a736f] ${helpOpen ? "rotate-90" : ""}`} />
          </button>
          {helpOpen && (
            <div className="border-t border-cream bg-cream-2/30" data-testid="menu-need-help-submenu">
              <MenuItem icon={LifeBuoy} onClick={() => soon("Get support")} testid="menu-get-support" indented>Get support</MenuItem>
              <MenuItem icon={BookOpen} onClick={() => soon("Help Center")} testid="menu-help-center" indented>Help Center</MenuItem>
              <MenuItem icon={Sparkles} onClick={releaseNotesJump} testid="menu-release-notes" indented>Release notes</MenuItem>
              <MenuItem icon={Activity} onClick={() => soon("Status")} testid="menu-status" indented>Status</MenuItem>
              <MenuItem icon={FileText} onClick={() => soon("Terms")} testid="menu-terms" indented>Terms</MenuItem>
              <MenuItem icon={Lock} onClick={() => soon("Privacy")} testid="menu-privacy" indented>Privacy</MenuItem>
            </div>
          )}

          <div className="border-t border-cream" />

          <MenuItem icon={ArrowLeftRight} onClick={() => { setOpen(false); onOpenWorkspaceSwitcher?.(); }} testid="menu-switch-accounts">
            Switch accounts
          </MenuItem>
          <MenuItem icon={LogOut} onClick={() => { setOpen(false); signOut?.(); nav("/"); }} testid="menu-sign-out" danger>
            Sign out
          </MenuItem>
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, onClick, children, danger, indented, trail, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-cream-2/60 ${
        danger ? "text-red-600 hover:text-red-700" : "text-forest"
      } ${indented ? "pl-8" : ""}`}
    >
      <span className="flex items-center gap-2">
        {Icon && <Icon size={14} className={danger ? "" : "text-[#6a736f]"} />}
        {children}
      </span>
      {trail && (
        <span className="text-[10px] font-semibold rounded-full bg-coral text-white px-1.5 py-0.5">{trail}</span>
      )}
    </button>
  );
}
